import type { ComponentPropsWithoutRef } from "react";
import { Link } from "react-router-dom";
import { Callout } from "./Callout";
import { Steps, Step } from "./Steps";

function isInternal(href: string | undefined): boolean {
  return !!href && href.startsWith("/");
}

export const mdxComponents = {
  h1: (props: ComponentPropsWithoutRef<"h1">) => (
    <h1 className="mb-4 mt-0 scroll-mt-24 font-display text-4xl font-bold text-bark-900" {...props} />
  ),
  h2: (props: ComponentPropsWithoutRef<"h2">) => (
    <h2 className="mb-3 mt-10 scroll-mt-24 font-display text-2xl font-semibold text-bark-900 [&_a]:text-bark-900 [&_a]:no-underline [&_a:hover]:text-forest-700" {...props} />
  ),
  h3: (props: ComponentPropsWithoutRef<"h3">) => (
    <h3 className="mb-2 mt-6 scroll-mt-24 font-display text-xl font-semibold text-bark-900 [&_a]:text-bark-900 [&_a]:no-underline [&_a:hover]:text-forest-700" {...props} />
  ),
  h4: (props: ComponentPropsWithoutRef<"h4">) => (
    <h4 className="mb-2 mt-5 scroll-mt-24 font-display text-lg font-semibold text-bark-900 [&_a]:text-bark-900 [&_a]:no-underline [&_a:hover]:text-forest-700" {...props} />
  ),
  p: (props: ComponentPropsWithoutRef<"p">) => (
    <p className="my-4 text-base leading-relaxed text-bark-800" {...props} />
  ),
  ul: (props: ComponentPropsWithoutRef<"ul">) => (
    <ul className="my-4 list-disc space-y-1.5 pl-6 text-bark-800 marker:text-forest-500" {...props} />
  ),
  ol: (props: ComponentPropsWithoutRef<"ol">) => (
    <ol className="my-4 list-decimal space-y-1.5 pl-6 text-bark-800 marker:text-forest-600 marker:font-semibold" {...props} />
  ),
  li: (props: ComponentPropsWithoutRef<"li">) => <li className="leading-relaxed" {...props} />,
  strong: (props: ComponentPropsWithoutRef<"strong">) => (
    <strong className="font-semibold text-bark-900" {...props} />
  ),
  em: (props: ComponentPropsWithoutRef<"em">) => <em className="italic" {...props} />,
  code: (props: ComponentPropsWithoutRef<"code">) => (
    <code className="rounded bg-cream-200 px-1.5 py-0.5 font-mono text-sm text-bark-900" {...props} />
  ),
  pre: (props: ComponentPropsWithoutRef<"pre">) => (
    <pre className="my-4 overflow-x-auto rounded-lg bg-forest-950 p-4 font-mono text-sm text-cream-100" {...props} />
  ),
  blockquote: (props: ComponentPropsWithoutRef<"blockquote">) => (
    <blockquote className="my-6 border-l-4 border-gold-400 bg-cream-100 px-5 py-3 italic text-bark-700" {...props} />
  ),
  hr: () => <hr className="my-10 border-t border-cream-300" />,
  table: (props: ComponentPropsWithoutRef<"table">) => (
    <div className="my-6 overflow-x-auto rounded-lg border border-cream-300">
      <table className="w-full border-collapse text-sm" {...props} />
    </div>
  ),
  th: (props: ComponentPropsWithoutRef<"th">) => (
    <th className="border-b border-cream-300 bg-cream-100 px-4 py-2 text-left font-semibold text-bark-900" {...props} />
  ),
  td: (props: ComponentPropsWithoutRef<"td">) => (
    <td className="border-b border-cream-200 px-4 py-2 text-bark-800" {...props} />
  ),
  a: ({ href, children, ...rest }: ComponentPropsWithoutRef<"a">) => {
    if (isInternal(href)) {
      return (
        <Link to={href!} className="text-forest-700 underline decoration-gold-400 decoration-2 underline-offset-4 hover:text-forest-800">
          {children}
        </Link>
      );
    }
    return (
      <a
        href={href}
        className="text-forest-700 underline decoration-gold-400 decoration-2 underline-offset-4 hover:text-forest-800"
        target={href?.startsWith("http") ? "_blank" : undefined}
        rel={href?.startsWith("http") ? "noreferrer" : undefined}
        {...rest}
      >
        {children}
      </a>
    );
  },
  Callout,
  Steps,
  Step,
};
